'use client';

import { styled } from 'styled-components';
import Logo6sense from './logos/6sense';
import LogoGong from './logos/Gong';
import LogoIntercom from './logos/Intercom';
import LogoRamp from './logos/Ramp';
import LogoShopify from './logos/Shopify';
import LogoSuperside from './logos/Superside';
import LogoUserTesting from './logos/UserTesting';
import LogoWorkiva from './logos/Workiva';
import LogoWriter from './logos/Writer';
import LogoZendesk from './logos/Zendesk';

export type CustomerLogoProps = {
  brands?: (
    | '6sense'
    | 'Gong'
    | 'Intercom'
    | 'Ramp'
    | 'Shopify'
    | 'Superside'
    | 'UserTesting'
    | 'Workiva'
    | 'Writer'
    | 'Zendesk'
  )[];

  /**
   * Optional class name, nesc. for extending as a styled-component
   */
  className?: string;
};

export const brandLogos = {
  '6sense': Logo6sense,
  Gong: LogoGong,
  Intercom: LogoIntercom,
  Ramp: LogoRamp,
  Shopify: LogoShopify,
  Superside: LogoSuperside,
  UserTesting: LogoUserTesting,
  Workiva: LogoWorkiva,
  Writer: LogoWriter,
  Zendesk: LogoZendesk,
};

const LogoWrapper = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing[1]};
  margin: auto;
  padding-bottom: ${({ theme }) => theme.spacing['1']};
  width: 100%;

  .logoList:hover .logoGroup {
    animation-play-state: paused;
  }
`;

const LogoList = styled.div.attrs({
  className: 'logoList',
})`
  display: flex;
  gap: ${({ theme }) => theme.spacing[3.75]};
  mask-image: linear-gradient(
    var(--mask-direction, to right),
    hsl(0deg 0% 0% / 0%),
    hsl(0deg 0% 0% / 100%) 20%,
    hsl(0deg 0% 0% / 100%) 80%,
    hsl(0deg 0% 0% / 0%)
  );
  overflow: hidden;
  user-select: none;

  @media (prefers-reduced-motion) {
    & div:nth-child(2) {
      /**  Hide the second set of logos when reduced motion is enabled. */
      display: none;
    }
  }
`;

const LogoGroup = styled.div.attrs({
  className: 'logoGroup',
})<{
  $brands: CustomerLogoProps['brands'];
}>`
  align-items: center;
  color: inherit;

  /* Speed up the animation time when we aren't rendering the full set of logos. */
  animation: scroll-logos
    ${({ $brands }) =>
      $brands.length < Object.values(brandLogos).length ? '15s' : '30s'}
    linear infinite;
  display: flex;
  flex-shrink: 0;
  gap: ${({ theme }) => theme.spacing[3.75]};
  justify-content: space-around;
  min-width: 100%;

  @media (prefers-reduced-motion) {
    animation: none;
    flex-wrap: wrap;
    width: fit-content;
    justify-content: flex-start;
  }

  @keyframes scroll-logos {
    from {
      transform: translateX(0);
    }

    to {
      transform: translateX(-100%);
    }
  }
`;

const CustomerLogos = ({
  brands = [
    '6sense',
    'Gong',
    'Intercom',
    'Ramp',
    'Shopify',
    'Superside',
    'UserTesting',
    'Workiva',
    'Writer',
    'Zendesk',
  ],
  className,
}: CustomerLogoProps) => {
  const brandItems = brands.map((brand) => {
    const Logo = brandLogos[brand];
    return <Logo key={brand} />;
  });

  return (
    <LogoWrapper className={className}>
      <LogoList>
        <LogoGroup $brands={brands}>{brandItems}</LogoGroup>
        <LogoGroup $brands={brands} aria-hidden="true">
          {brandItems}
        </LogoGroup>
      </LogoList>
    </LogoWrapper>
  );
};

export default CustomerLogos;
